/**
 * GRIDHEIMR Design System
 *
 * Single source of truth for all visual constants, reusable style objects,
 * and the font loader helper used across the dashboard.
 */

import type React from "react";

/* ── Palette ─────────────────────────────────────────────────────── */
export const BLUE = "#006AA7";
export const YELLOW = "#FECC00";
export const GREEN = "#00c97a";
export const RED = "#ef4444";
export const DARK_BG = "#000a12";

/* ── Typography ──────────────────────────────────────────────────── */
export const PIXEL = "'Press Start 2P', monospace";
export const MONO = "'Space Mono', 'Courier New', monospace";
export const FONT_URL =
  "https://fonts.googleapis.com/css2?family=Press+Start+2P&family=Space+Mono:wght@400;700&display=swap";

/* ── Font loader ─────────────────────────────────────────────────── */
export function ensureFonts(id: string): void {
  if (!document.getElementById(id)) {
    const l = Object.assign(document.createElement("link"), {
      id,
      rel: "stylesheet",
      href: FONT_URL,
    });
    document.head.appendChild(l);
  }
}

/* ── Reusable style objects ──────────────────────────────────────── */
type S = React.CSSProperties;

export const buttonPrimary: S = {
  fontFamily: PIXEL,
  fontSize: "0.55rem",
  padding: "12px 20px",
  background: YELLOW,
  color: "#000",
  border: "none",
  cursor: "pointer",
  letterSpacing: "0.1em",
  transition: "all 0.2s ease",
  boxShadow: `0 0 12px rgba(254,204,0,0.25)`,
};

export const buttonSecondary: S = {
  fontFamily: PIXEL,
  fontSize: "0.55rem",
  padding: "12px 20px",
  background: "transparent",
  color: BLUE,
  border: `2px solid ${BLUE}`,
  cursor: "pointer",
  letterSpacing: "0.1em",
  transition: "all 0.2s ease",
  boxShadow: `0 0 8px rgba(0,106,167,0.2)`,
};

export const buttonDanger: S = {
  fontFamily: PIXEL,
  fontSize: "0.55rem",
  padding: "12px 20px",
  background: "transparent",
  color: RED,
  border: `2px solid ${RED}`,
  cursor: "pointer",
  letterSpacing: "0.1em",
  transition: "all 0.2s ease",
};

export const buttonGhost: S = {
  fontFamily: PIXEL,
  fontSize: "0.55rem",
  padding: "10px 16px",
  background: "transparent",
  color: "rgba(255,255,255,0.5)",
  border: `1px solid rgba(0,106,167,0.3)`,
  cursor: "pointer",
  letterSpacing: "0.1em",
  transition: "all 0.2s ease",
};

export const inputField: S = {
  fontFamily: MONO,
  fontSize: "0.9rem",
  color: "#fff",
  background: "rgba(0,106,167,0.06)",
  border: `1px solid rgba(0,106,167,0.25)`,
  padding: "10px 14px",
  transition: "all 0.2s ease",
  outline: "none",
};

export const selectField: S = {
  ...inputField,
  cursor: "pointer",
};

export const panel: S = {
  background: "rgba(0,10,18,0.85)",
  border: `1px solid rgba(0,106,167,0.2)`,
  padding: "1.5rem",
};

export const errorBanner: S = {
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
  background: "rgba(239,68,68,0.1)",
  border: "1px solid rgba(239,68,68,0.3)",
  padding: "1rem",
  marginBottom: "1.5rem",
};

export const successBanner: S = {
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
  background: `rgba(0,201,122,0.1)`,
  border: `1px solid rgba(0,201,122,0.3)`,
  padding: "1rem",
  marginBottom: "1.5rem",
};

export const infoBanner: S = {
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
  background: `rgba(0,106,167,0.1)`,
  border: `1px solid rgba(0,106,167,0.3)`,
  padding: "1rem",
};

export const pixelLabel: S = {
  fontFamily: PIXEL,
  fontSize: "0.42rem",
  color: YELLOW,
  letterSpacing: "0.15em",
  textTransform: "uppercase",
  textShadow: `0 0 8px rgba(254,204,0,0.3)`,
};

export const sectionComment: S = {
  fontFamily: MONO,
  fontSize: "0.7rem",
  color: "rgba(255,255,255,0.3)",
  letterSpacing: "0.15em",
  margin: "0 0 0.75rem 0",
};

export const pageTitle: S = {
  fontFamily: PIXEL,
  fontSize: "clamp(0.8rem, 2.5vw, 1.2rem)",
  fontWeight: 400,
  margin: "0 0 0.75rem 0",
  color: "#fff",
  letterSpacing: "0.06em",
  textShadow: `0 0 15px rgba(0,106,167,0.4)`,
};

export const monoText: S = {
  fontFamily: MONO,
  color: "rgba(255,255,255,0.6)",
  letterSpacing: "0.05em",
};

export const badge: S = {
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 10px",
  border: "1px solid",
  fontFamily: PIXEL,
  fontSize: "0.42rem",
  letterSpacing: "0.08em",
  lineHeight: 1.4,
};
